import React from "react";
import PropTypes from "prop-types";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { theme } from "../../../utils/global-styles/theme";
import { Link } from "gatsby";
import FoldyMapIcon from "../../shared/CourseBenefitIcons/FoldyMapIcon";
import BadgeIcon from "../../shared/CourseBenefitIcons/BadgeIcon";
import WavesIcon from "../../shared/CourseBenefitIcons/WavesIcon";

const CourseBenefits = ({ courseBenefits, className, page = null }) => {
  const CourseBenefitsContainer = styled.section`
      height: auto;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      padding: 50px 4%;
      font-family: ${theme.fontFaces.headers};

      @media only screen and (max-width: ${theme.breakpoints.iPadPro}) {
        flex-direction: column;
        align-items: center;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        padding: 60px 0px;
      }
    `,
    CourseBenefitGrid = styled.div`
      height: 100%;
      width: 40%;
      display: grid;
      grid-template-rows: 150px auto;
      grid-template-columns: 1fr;

      @media only screen and (max-width: ${theme.breakpoints.helper1100}) {
        grid-template-rows: 120px auto;
        margin-bottom: 40px;
        padding-top: 40px;
        width: 55%;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        display: block;
        width: 70%;
        margin-bottom: 40px;
      }

      @media only screen and (min-width: ${theme.breakpoints.desktopUp}) {
        width: 20%;
      }
    `,
    BenefitIconContainer = styled.div`
      grid-area: 1 / 1 / span 1 / span 1;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      padding-bottom: 20px;
    `,
    BenefitTitleContainer = styled.div`
      grid-area: 1 / 1 / span 1 / span 1;
    `,
    BenefitTitle = styled.h4`
      font-weight: 500;
      color: ${theme.colors.black};
      letter-spacing: -0.22px;
      text-align: center;
      text-transform: capitalize;

      @media only screen and (max-width: ${theme.breakpoints.helper1350}) {
        padding-bottom: 20px;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        padding-bottom: 10px;
        font-weight: 500;
        letter-spacing: -0.18px;
      }
    `,
    BenefitTextContainer = styled.div``,
    BenefitText = styled.p`
      font-weight: 500;
      letter-spacing: -0.37px;
      text-align: center;
      color: ${theme.colors.black};
      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-weight: 500;
        letter-spacing: -0.3px;
        text-align: center;
      }
    `,
    CtaButton = styled(Link)`
      height: 36px;
      width: 166px;
      font-size: 14px;
      font-weight: bold;
      margin: 0 auto;
      letter-spacing: 0.4px;
      line-height: 15px;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 2px solid;
      margin-top: 20px;
    `;

  const createCourseBenefitsArray = (courseBenefits) => {
      if (!courseBenefits) {
        return []; // Return an empty array if courseBenefits is undefined or null
      }

      const outputArray = [],
        courseBenefitsLength = Object.keys(courseBenefits).length / 2;
      for (let i = 1; i < courseBenefitsLength + 1; i++) {
        let benefit = {
          title: courseBenefits[`benefit${i}`],
          description: courseBenefits[`description${i}`],
          iconImage: courseBenefits[`icon${i}Image`],
          cta: courseBenefits[`cta${i}`],
        };
        if (benefit.title && benefit.title.length) {
          outputArray.push(benefit);
        }
      }
      return outputArray;
    },
    determineBenefitIcon = (index, iconImage) => {
      if (iconImage) {
        return (
          <GatsbyImage
            image={getImage(iconImage?.gatsbyImageData)}
            css={css`
              height: 35px;
              width: 35px;
            `}
          />
        );
      }
      switch (index) {
        case 0:
          return <FoldyMapIcon width="35px" height="35px" />;
        case 1:
          return <BadgeIcon width="28px" height="36px" />;
        case 2:
          return <WavesIcon width="36px" height="31px" />;
        default:
          return <WavesIcon width="36px" height="31px" />;
      }
    };

  return (
    <CourseBenefitsContainer className={`primary-bg white ${className || ""}`}>
      {createCourseBenefitsArray(courseBenefits).map((benefit, index) => {
        return (
          <CourseBenefitGrid key={index}>
            <BenefitTitleContainer>
              <BenefitTitle>{benefit?.title}</BenefitTitle>
            </BenefitTitleContainer>
            {benefit.description && (
              <BenefitTextContainer>
                <BenefitText>{benefit.description}</BenefitText>
              </BenefitTextContainer>
            )}
            {page === "community" && benefit.cta && (
              <CtaButton className="white" to={`${benefit.cta?.url}/`}>
                {benefit.cta?.linkText}
              </CtaButton>
            )}
          </CourseBenefitGrid>
        );
      })}
    </CourseBenefitsContainer>
  );
};

CourseBenefits.propTypes = {
  courseBenefits: PropTypes.shape({
    icon1Image: PropTypes.shape({
      fluid: PropTypes.shape({
        srcSet: PropTypes.string,
        aspectRatio: PropTypes.number,
      }),
    }),
    benefit1: PropTypes.string,
    description1: PropTypes.string,
    icon2Image: PropTypes.shape({
      fluid: PropTypes.shape({
        srcSet: PropTypes.string,
        aspectRatio: PropTypes.number,
      }),
    }),
    benefit2: PropTypes.string,
    description2: PropTypes.string,
    icon3Image: PropTypes.shape({
      fluid: PropTypes.shape({
        srcSet: PropTypes.string,
        aspectRatio: PropTypes.number,
      }),
    }),
    benefit3: PropTypes.string,
    description3: PropTypes.string,
    icon4Image: PropTypes.shape({
      fluid: PropTypes.shape({
        srcSet: PropTypes.string,
        aspectRatio: PropTypes.number,
      }),
    }),
    benefit4: PropTypes.string,
    description4: PropTypes.string,
  }),
  className: PropTypes.string,
};

export default CourseBenefits;
